import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';

export type Role = 'admin' | 'reviewer' | 'fbc';

export interface AuthUser {
  id: number;
  email: string;
  name: string | null;
  role: Role;
  status: 'invited' | 'active' | 'disabled';
  last_login_at: string | null;
  created_at: string;
}

export interface Impersonator {
  id: number;
  email: string;
  name: string;
}

interface AuthState {
  user: AuthUser | null;
  impersonator: Impersonator | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  stopImpersonating: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [impersonator, setImpersonator] = useState<Impersonator | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const res = await api<{ user: AuthUser; impersonator: Impersonator | null }>('/api/auth/me');
      setUser(res.user);
      setImpersonator(res.impersonator);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setImpersonator(null);
      } else {
        throw err;
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api('/api/auth/logout', { method: 'POST' });
    setUser(null);
    setImpersonator(null);
  };

  const stopImpersonating = async () => {
    await api('/api/auth/stop-impersonating', { method: 'POST' });
    await refresh();
  };

  useEffect(() => { refresh(); }, []);

  return (
    <Ctx.Provider value={{ user, impersonator, loading, refresh, logout, stopImpersonating }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
